import type { ReactElement } from 'react';
import styled from 'styled-components';
import {
  APIEntityType,
  type APIComponentAttributes,
  type ArdoqId,
  type UserDataForRenderers,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import {
  ComponentRenderer,
  FieldType,
  RedactedFieldDisplay,
  RenderOption,
  getFieldLabel,
  type RenderOptions,
} from '@ardoq/renderers';
import { FlexBox, Stack } from '@ardoq/layout';
import { Paragraph, caption } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import {
  permissionsOperations,
  type PermissionContext,
} from '@ardoq/access-control';

const renderOptions: RenderOptions = {
  types: {
    [FieldType.TEXT_AREA]: new Set([RenderOption.MARKDOWN]),
  },
  definitions: {},
};

const DetailRow = styled(FlexBox).attrs({
  gap: 'medium',
  width: 'full',
  justify: 'space-between',
  align: 'start',
  paddingY: 'small',
})`
  border-top: 1px solid ${colors.borderSubtle};
`;

const DetailValue = styled(FlexBox).attrs({
  justify: 'flex-end',
})`
  ${caption}
  flex: 1 1 0;
  min-width: 0;
  text-align: right;
`;

const toUsersLookup = (
  enhancedScopeData: EnhancedScopeData
): Record<ArdoqId, UserDataForRenderers> =>
  Object.fromEntries(enhancedScopeData.users.map(user => [user._id, user]));

type PreviewDetailRowProps = {
  fieldName: string;
  component: APIComponentAttributes;
  enhancedScopeData: EnhancedScopeData;
  users: Record<ArdoqId, UserDataForRenderers>;
  permissionContext: PermissionContext;
};

const PreviewDetailRow = ({
  fieldName,
  component,
  enhancedScopeData,
  users,
  permissionContext,
}: PreviewDetailRowProps): ReactElement => {
  const isConfidential = !permissionsOperations.canSeeConfidentialFieldByName(
    permissionContext,
    fieldName,
    component.rootWorkspace
  );

  return (
    <DetailRow>
      <Paragraph variant="captionBold">
        {getFieldLabel({
          fieldName,
          enhancedScopeData,
          entityType: APIEntityType.COMPONENT,
        })}
      </Paragraph>
      <DetailValue>
        {isConfidential ? (
          <RedactedFieldDisplay />
        ) : (
          <ComponentRenderer
            entityId={component._id}
            fieldName={fieldName}
            enhancedScopeData={enhancedScopeData}
            users={users}
            options={renderOptions}
          />
        )}
      </DetailValue>
    </DetailRow>
  );
};

type PreviewDetailsProps = {
  detailNames: string[];
  component: APIComponentAttributes;
  enhancedScopeData: EnhancedScopeData;
  permissionContext: PermissionContext;
};

export const PreviewDetails = ({
  detailNames,
  component,
  enhancedScopeData,
  permissionContext,
}: PreviewDetailsProps): ReactElement => {
  const users = toUsersLookup(enhancedScopeData);

  return (
    <Stack gap="none">
      {detailNames.map(fieldName => (
        <PreviewDetailRow
          key={fieldName}
          fieldName={fieldName}
          component={component}
          enhancedScopeData={enhancedScopeData}
          users={users}
          permissionContext={permissionContext}
        />
      ))}
    </Stack>
  );
};
